import { Text, View } from "@react-pdf/renderer";
import { getTenantStore } from "../../../../stores/tenantStore";
import { formatDateTimeUtc } from "../../../../utils/date_formatter";
import { formatResponsiblePerson } from "../utils";
import { buildReportingHubFilePreviewUrl } from "../../../../utils/reports";
import { styles } from "../../ReactPdfStyles";
import ReportingApprovalRecordSection from "../ReportingApprovalRecordSection";
import ReportingFileList from "../ReportingFileList";
import ReportingProjectDetailSection from "../ReportingProjectDetailSection";
import ReportPdfLayout from "../ReportPdfLayout";

interface MocPDFTemplateProps {
	data: Record<string, any>;
}

function MocPDFTemplate({ data }: MocPDFTemplateProps) {
	return (
		<ReportPdfLayout reportTitle="MANAGEMENT OF CHANGE" data={data}>
			<ReportingProjectDetailSection data={data} />
			<DetailSection data={data} />
			<ReportingApprovalRecordSection data={data} />
			<FollowUpActionsSection
				follow_up_actions={data.follow_up_actions ?? []}
			/>
		</ReportPdfLayout>
	);
}

export default MocPDFTemplate;

function DetailSection({ data }: { data: Record<string, any> }) {
	const dateOriginated = formatDateTimeUtc(data?.submitted_on ?? "") || "—";
	const department = data?.department_name ?? data?.department ?? "—";
	const categoryName = data?.category_name ?? data?.category ?? "—";
	const briefDescription = data?.description ?? "—";
	const attachmentComments = data?.attachment_comments ?? "—";

	const tenantStore = getTenantStore();
	const tenantList = tenantStore.getTenantList();

	const containerId = tenantList.find(
		(tenant: any) => tenant.role_key === data?.operator
	)?.container_id;

	const renderFiles = () => {
		const files = data.additional_files;
		if (!files.length) return <Text>—</Text>;
		const origin = window.location.origin;
		const previewUrl = `${origin}/reporting/moc-stream/${data._id}/preview`;
		return (
			<ReportingFileList
				files={files}
				buildUrl={(fileObj: any) =>
					containerId
						? buildReportingHubFilePreviewUrl(previewUrl, fileObj, containerId)
						: null
				}
			/>
		);
	};

	return (
		<View style={[styles.section, styles.tableFontSize]}>
			<Text style={styles.tableHeader}>MOC detail</Text>
			<View style={styles.row}>
				<View style={{ flex: 1 }}>
					<View style={styles.row}>
						<Text style={styles.labelCell}>Date originated</Text>
						<Text style={styles.valueCell}>{dateOriginated}</Text>
					</View>
					<View style={styles.row}>
						<Text style={styles.labelCell}>Department</Text>
						<Text style={styles.valueCell}>{department}</Text>
					</View>
				</View>

				<View style={[styles.row, { flex: 1 }]}>
					<Text style={styles.labelCell}>MoC Category</Text>
					<Text style={[styles.valueCell, styles.borderRight]}>
						{categoryName}
					</Text>
				</View>
			</View>

			<View style={styles.row}>
				<Text style={styles.labelCell}>Brief description</Text>
				<Text style={[styles.valueCell, styles.borderRight]}>
					{briefDescription}
				</Text>
			</View>

			<View style={styles.row}>
				<Text style={styles.labelCell}>File upload(s)</Text>
				<View style={[styles.valueCell, styles.borderRight]}>
					{renderFiles()}
				</View>
			</View>

			<View style={styles.row}>
				<Text style={styles.labelCell}>Attachment comments</Text>
				<Text style={[styles.valueCell, styles.borderRight]}>
					{attachmentComments}
				</Text>
			</View>
		</View>
	);
}

function FollowUpActionsSection({
	follow_up_actions,
}: {
	follow_up_actions: any[];
}) {
	const renderCompletedBy = (action: any) => {
		const responsibleDisplay = formatResponsiblePerson(
			action?.responsible,
			action?.responsible_name
		);
		const completed = action.completed_on
			? `${formatDateTimeUtc(action.completed_on as any)}`
			: null;
		return (
			<View style={styles.column}>
				{responsibleDisplay && (
					<View style={{ marginBottom: 8 }}>
						<Text
							style={{
								fontWeight: "bold",
							}}
							wrap={false}
						>
							Person Responsible:
						</Text>
						<Text>{responsibleDisplay}</Text>
					</View>
				)}
				{completed ? (
					<>
						<View style={{ marginBottom: 8 }}>
							<Text style={{ fontWeight: "bold" }}>Completed by:</Text>
							<Text>{`${responsibleDisplay} (${completed})`}</Text>
						</View>
					</>
				) : (
					<Text style={{ fontStyle: "italic" }}>Pending completion</Text>
				)}

				{/* Spacing */}
				<View style={{ height: 6 }} />

				{/* Closing comment section */}
				{action.closing_comment && (
					<>
						<Text style={{ fontWeight: "bold" }}>Closing comment:</Text>
						<Text>{action.closing_comment}</Text>
					</>
				)}
			</View>
		);
	};
	return (
		<View break style={[styles.section, styles.tableFontSize]}>
			<Text style={[styles.tableHeader, { marginBottom: 15 }]}>Actions</Text>
			<View style={styles.row}>
				<Text
					style={[
						styles.borderTop,
						styles.borderLeft,
						{
							flex: 1,
							fontWeight: "bold",
							backgroundColor: "#F2F2F2",
							padding: 8,
						},
					]}
				>
					Action no.
				</Text>
				<Text
					style={[
						styles.borderTop,
						styles.borderLeft,
						{
							flex: 1,
							fontWeight: "bold",
							backgroundColor: "#F2F2F2",
							padding: 8,
						},
					]}
				>
					Description
				</Text>
				<Text
					style={[
						styles.borderTop,
						styles.borderLeft,
						styles.borderRight,
						{
							flex: 1,
							fontWeight: "bold",
							backgroundColor: "#F2F2F2",
							padding: 8,
						},
					]}
				>
					Remarks
				</Text>
			</View>
			<View>
				{Array.isArray(follow_up_actions) && follow_up_actions.length > 0 ? (
					follow_up_actions.map((action: any) => (
						<View key={action.action_number} style={styles.row}>
							<Text style={[styles.valueCell, styles.borderLeft]}>
								{action.action_number ?? "—"}
							</Text>
							<Text style={[styles.valueCell, styles.borderLeft]}>
								{action.description ?? "—"}
							</Text>
							<View
								style={[
									styles.valueCell,
									styles.borderLeft,
									styles.borderRight,
								]}
							>
								{renderCompletedBy(action)}
							</View>
						</View>
					))
				) : (
					<View style={styles.row}>
						<Text
							style={[
								styles.valueCell,
								styles.borderLeft,
								styles.borderRight,
								{ textAlign: "center" },
							]}
						>
							No follow-up actions available
						</Text>
					</View>
				)}
			</View>
		</View>
	);
}
